/**
 * OrganizationCache - Facade for organization data caching
 * Refactored to use focused classes following Single Responsibility Principle
 *
 * Architecture:
 * - CacheStorage: localStorage persistence
 * - CacheManager: Core caching operations (get, set, invalidate)
 * - CacheArrayOperations: Array-specific operations
 * - OrganizationCache: Facade that coordinates all operations + preloading
 */

import logger from '../../../shared/services/logging/logger';
import { CACHE_SIZE_LIMIT } from '../config/cacheConfig';
import { CacheStorage } from './CacheStorage';
import { CacheManager } from './CacheManager';
import { CacheArrayOperations } from './CacheArrayOperations';

class OrganizationCache {
  constructor() {
    // Initialize focused classes
    this.storage = new CacheStorage();
    this.manager = new CacheManager(this.storage);
    this.arrayOps = new CacheArrayOperations(this.manager);

    // Setup beforeunload handler for immediate save
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.manager.forceSave();
      });
    }
  }

  // ============================================================================
  // CORE CACHE OPERATIONS - Delegate to CacheManager
  // ============================================================================

  /**
   * Generate cache key for organization data
   */
  getCacheKey(orgId, dataType, subKey = '') {
    return this.manager.getCacheKey(orgId, dataType, subKey);
  }

  /**
   * Get cached data
   */
  get(dataType, orgId, subKey = '') {
    return this.manager.get(dataType, orgId, subKey);
  }

  /**
   * Set cached data
   */
  set(dataType, orgId, data, subKey = '') {
    this.manager.set(dataType, orgId, data, subKey);
    this.dispatchCacheUpdate(dataType, orgId, data);
  }

  /**
   * Check if data is currently loading
   */
  isLoading(dataType, orgId, subKey = '') {
    return this.manager.isLoading(dataType, orgId, subKey);
  }

  /**
   * Set loading state
   */
  setLoading(dataType, orgId, isLoading = true, subKey = '') {
    this.manager.setLoading(dataType, orgId, isLoading, subKey);
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(dataType, orgId, subKey = '') {
    return this.manager.invalidate(dataType, orgId, subKey);
  }

  /**
   * Invalidate all cache for an organization
   */
  invalidateOrganization(orgId) {
    return this.manager.invalidateOrganization(orgId);
  }

  /**
   * Clear all cache
   */
  clearAll() {
    this.manager.clearAll();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const baseStats = this.manager.getStats();

    // Add utilization percentage
    baseStats.utilizationPercent = Math.round((baseStats.totalEntries / CACHE_SIZE_LIMIT) * 100);

    return baseStats;
  }

  // ============================================================================
  // ARRAY OPERATIONS - Delegate to CacheArrayOperations
  // ============================================================================

  /**
   * Remove specific item from cached array
   */
  removeFromCachedArray(dataType, orgId, itemIdentifier, identifierField = 'notificationId', subKey = '') {
    const result = this.arrayOps.removeFromCachedArray(dataType, orgId, itemIdentifier, identifierField, subKey);
    if (result) {
      const updatedData = this.manager.get(dataType, orgId, subKey);
      this.dispatchCacheUpdate(dataType, orgId, updatedData);
    }
    return result;
  }

  /**
   * Add item to cached array
   */
  addToCachedArray(dataType, orgId, newItem, subKey = '') {
    const result = this.arrayOps.addToCachedArray(dataType, orgId, newItem, subKey);
    if (result) {
      const updatedData = this.manager.get(dataType, orgId, subKey);
      this.dispatchCacheUpdate(dataType, orgId, updatedData);
    }
    return result;
  }

  /**
   * Update item in cached array
   */
  updateInCachedArray(dataType, orgId, itemIdentifier, updatedItem, identifierField = 'notificationId', subKey = '') {
    const result = this.arrayOps.updateInCachedArray(dataType, orgId, itemIdentifier, updatedItem, identifierField, subKey);
    if (result) {
      const updatedData = this.manager.get(dataType, orgId, subKey);
      this.dispatchCacheUpdate(dataType, orgId, updatedData);
    }
    return result;
  }

  /**
   * Mark notification as read in cache
   */
  markNotificationAsRead(userId, notificationId) {
    const result = this.arrayOps.markNotificationAsRead(userId, notificationId);
    if (result) {
      const updatedData = this.manager.get('notifications', userId);
      this.dispatchCacheUpdate('notifications', userId, updatedData);
    }
    return result;
  }

  /**
   * Mark all notifications as read in cache
   */
  markAllNotificationsAsRead(userId) {
    const result = this.arrayOps.markAllNotificationsAsRead(userId);
    if (result) {
      const updatedData = this.manager.get('notifications', userId);
      this.dispatchCacheUpdate('notifications', userId, updatedData);
    }
    return result;
  }

  // ============================================================================
  // PRELOADING OPERATIONS - High-level orchestration (stays in facade)
  // ============================================================================

  /**
   * Preload organization data
   */
  async preloadOrganizationData(orgId, services = {}) {
    const {
      RoleService,
      MemberService,
      DivisionService,
      GroupService,
      InvitationService,
      OrganizationService,
    } = services;

    const preloadTasks = [];

    // Preload organization details and theme FIRST (highest priority)
    if (OrganizationService && !this.get('organization', orgId)) {
      preloadTasks.push(
        this.preloadData('organization', orgId, async () => {
          try {
            // Try to get both theme and details, but don't fail if one fails
            const [themeResult, detailsResult] = await Promise.allSettled([
              OrganizationService.getTheme(orgId),
              OrganizationService.getOrganizationDetails(orgId)
            ]);

            const organizationData = {};

            // Handle theme data
            if (themeResult.status === 'fulfilled' && themeResult.value?.success) {
              organizationData.theme = themeResult.value.data?.orgTheme || themeResult.value.data?.theme;
            } else {
              organizationData.theme = '#9404C3'; // Default theme
            }

            // Handle details data
            if (detailsResult.status === 'fulfilled' && detailsResult.value?.success) {
              organizationData.details = detailsResult.value.data;
            } else {
              logger.warn('Failed to load organization details during preload', {
                orgId,
                status: detailsResult.status,
                reason: detailsResult.reason?.message
              });
              organizationData.details = null;
            }

            return organizationData;
          } catch (error) {
            // Return minimal data with default theme
            return { theme: '#9404C3' };
          }
        })
      );
    }

    // Preload roles
    if (RoleService && !this.get('roles', orgId)) {
      preloadTasks.push(
        this.preloadData('roles', orgId, () => RoleService.getAllRoles(orgId))
      );
    }

    // Preload divisions
    if (DivisionService && !this.get('divisions', orgId)) {
      preloadTasks.push(
        this.preloadData('divisions', orgId, async () => {
          const divisions = await DivisionService.getOrganizationDivisions(orgId);
          // Division members will be loaded on-demand when needed
          return divisions;
        })
      );
    }

    // Preload groups
    if (GroupService && !this.get('groups', orgId)) {
      preloadTasks.push(
        this.preloadData('groups', orgId, async () => {
          const groups = await GroupService.getOrganizationGroups(orgId);
          // Group members will be loaded on-demand when needed
          return groups;
        })
      );
    }

    // Preload invitations
    if (InvitationService && !this.get('invitations', orgId)) {
      preloadTasks.push(
        this.preloadData('invitations', orgId, () => InvitationService.getInvitations({ status: 'PENDING' }))
      );
    }

    // Preload members (low priority, loaded last)
    if (MemberService && !this.get('members', orgId)) {
      preloadTasks.push(
        this.preloadData('members', orgId, () => MemberService.getOrganizationMembersWithAssociations(orgId))
      );
    }

    try {
      await Promise.allSettled(preloadTasks);
      logger.info('Organization data preloaded successfully', {
        orgId,
        tasksCount: preloadTasks.length
      });
    } catch (error) {
      logger.error('Error during organization data preload', {
        error: error.message,
        orgId
      });
    }
  }

  /**
   * Helper method to preload specific data type
   */
  async preloadData(dataType, orgId, fetchFunction) {
    try {
      this.setLoading(dataType, orgId, true);
      const data = await fetchFunction();
      this.set(dataType, orgId, data);
    } catch (error) {
      logger.error('Failed to preload data', { error: error.message, dataType, orgId });
      throw error;
    } finally {
      this.setLoading(dataType, orgId, false);
    }
  }

  // ============================================================================
  // EVENT DISPATCHING - Custom events for cache updates
  // ============================================================================

  /**
   * Dispatch cache update event
   */
  dispatchCacheUpdate(dataType, orgId, data) {
    try {
      const event = new CustomEvent('organizationCacheUpdated', {
        detail: {
          dataType,
          orgId,
          data,
          cacheInfo: this.getStats()
        }
      });
      window.dispatchEvent(event);
    } catch (error) {
      logger.warn('Failed to dispatch cache update event', { error: error.message, dataType, orgId });
    }
  }
}

// Export singleton instance
const organizationCache = new OrganizationCache();
export default organizationCache;
